import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "wouter";
import { io, Socket } from "socket.io-client";
import { getApiUrl, API_URL, ApiError } from "@/lib/api";
import {
  isTerminalTrackingStatus,
  type TrackingSessionStatus,
} from "@shared/tracking";

interface PublicSession {
  id: string;
  busNumber: string;
  eventName: string;
  status: TrackingSessionStatus;
}

type PageState = "loading" | "error" | "ready" | "tracking";

interface CoordInfo {
  lat: number;
  lng: number;
  label?: string;
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function haversineKm(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function kmToMiles(km: number): string {
  const mi = km * 0.621371;
  return mi < 0.1 ? "< 0.1 mi" : `${mi.toFixed(1)} mi`;
}

export function TrackDriverPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const token = new URLSearchParams(window.location.search).get("token") ?? "";

  // Custom-scheme deep link into the native Driver app. HTTPS App Links open
  // the app automatically on a normal SMS tap, but in-app browsers
  // (WhatsApp / SMS WebViews) bypass App Links and land here instead — this
  // scheme link is the manual escape hatch so drivers stop copy/pasting.
  const appDeepLink = `rutalink-driver://track/${sessionId}/driver?token=${token}`;

  const [state, setState] = useState<PageState>("loading");
  const [session, setSession] = useState<PublicSession | null>(null);
  const [errorMsg, setErrorMsg] = useState(
    "This tracking link has expired or is invalid."
  );

  // Tracking state
  const [tokenError, setTokenError] = useState(false);
  const [wakeLockWarn, setWakeLockWarn] = useState(false);
  const [gpsError, setGpsError] = useState(false);
  const [coordInfo, setCoordInfo] = useState<CoordInfo | null>(null);
  const [statusBanner, setStatusBanner] = useState<string | null>(null);
  const [allAboardModal, setAllAboardModal] = useState(false);
  const [myLat, setMyLat] = useState<number | null>(null);
  const [myLng, setMyLng] = useState<number | null>(null);

  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const watchIdRef = useRef<number | null>(null);
  const socketRef = useRef<Socket | null>(null);

  // Load session on mount
  useEffect(() => {
    if (!sessionId || !UUID_RE.test(sessionId)) {
      setErrorMsg("This tracking link has expired or is invalid.");
      setState("error");
      return;
    }

    let cancelled = false;

    fetch(getApiUrl(`/api/tracking/sessions/${sessionId}/public`))
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new ApiError(
            res.status,
            (body as { message?: string }).message || "Not found"
          );
        }
        return res.json() as Promise<PublicSession>;
      })
      .then((data) => {
        if (cancelled) return;
        // Cancelled sessions are treated exactly like ended — tracking is over
        if (isTerminalTrackingStatus(data.status)) {
          setErrorMsg("This event has ended. No further tracking is available.");
          setState("error");
          return;
        }
        setSession(data);
        setState("ready");
      })
      .catch(() => {
        if (!cancelled) {
          setErrorMsg(
            "Unable to load tracking info. Please check your connection."
          );
          setState("error");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  // Re-acquire Wake Lock when tab becomes visible
  const reacquireWakeLock = useCallback(async () => {
    if (
      wakeLockRef.current !== null &&
      wakeLockRef.current.released &&
      "wakeLock" in navigator &&
      document.visibilityState === "visible"
    ) {
      try {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
      } catch {
        // Silently fail — wake lock is best-effort
      }
    }
  }, []);

  useEffect(() => {
    document.addEventListener("visibilitychange", reacquireWakeLock);
    return () =>
      document.removeEventListener("visibilitychange", reacquireWakeLock);
  }, [reacquireWakeLock]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      socketRef.current?.disconnect();
      wakeLockRef.current?.release().catch(() => {});
    };
  }, []);

  const startTracking = useCallback(async () => {
    if (!session || !sessionId) return;

    // 1. Wake Lock
    if ("wakeLock" in navigator) {
      try {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
      } catch {
        setWakeLockWarn(true);
      }
    } else {
      setWakeLockWarn(true);
    }

    // 2. Socket.IO — namespace must be in the URL, not in options
    const base = API_URL || window.location.origin;
    const socket = io(`${base}/tracking`, {
      path: "/socket.io",
      transports: ["websocket", "polling"],
    });
    socketRef.current = socket;

    // Join inside the connect handler so the session is re-joined after every
    // reconnect — server-side join state dies with the old connection, and
    // un-joined location emits are silently dropped
    socket.on("connect", () => {
      socket.emit("join-tracking-session", {
        sessionId,
        role: "driver",
        token,
      });
    });

    socket.on("join-error", ({ message }: { message: string }) => {
      console.warn("join-error:", message);
      setTokenError(true);
    });

    socket.on(
      "coordinator-location",
      ({ lat, lng }: { lat: number; lng: number }) => {
        setCoordInfo((prev) => ({ ...prev, lat, lng, label: prev?.label }));
      }
    );

    socket.on(
      "meetup-update",
      ({ lat, lng, label }: { lat: number; lng: number; label?: string }) => {
        setCoordInfo({ lat, lng, label });
      }
    );

    socket.on("status-broadcast", ({ message }: { message: string }) => {
      setStatusBanner(message);
    });

    socket.on("all-aboard", () => {
      setAllAboardModal(true);
    });

    // 3. GPS watchPosition
    if (!navigator.geolocation) {
      setGpsError(true);
      setState("tracking");
      return; // socket already stored in ref — cleanup useEffect handles disconnect
    }

    setState("tracking");

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude: lat, longitude: lng, accuracy } = pos.coords;
        setMyLat(lat);
        setMyLng(lng);
        socket.emit("driver-location", {
          lat,
          lng,
          accuracy,
          timestamp: Date.now(),
        });
      },
      (err) => {
        // Handle all GPS error types: PERMISSION_DENIED, POSITION_UNAVAILABLE, TIMEOUT
        if (
          err.code === err.PERMISSION_DENIED ||
          err.code === err.POSITION_UNAVAILABLE ||
          err.code === err.TIMEOUT
        ) {
          setGpsError(true);
        }
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 }
    );
  }, [session, sessionId, token]);

  // --- Render ---

  if (state === "loading") {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div
          role="status"
          aria-label="Loading"
          className="w-8 h-8 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin"
        />
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6">
        <div className="bg-gray-800 rounded-2xl p-8 max-w-sm w-full text-center">
          <div className="text-5xl mb-4">🚫</div>
          <h1 className="text-xl font-bold text-white mb-2">
            Link Unavailable
          </h1>
          <p className="text-gray-400 text-sm">{errorMsg}</p>
        </div>
      </div>
    );
  }

  if (state === "tracking") {
    const coordDistance =
      coordInfo && myLat !== null && myLng !== null
        ? kmToMiles(haversineKm(myLat, myLng, coordInfo.lat, coordInfo.lng))
        : null;

    return (
      <div className="min-h-screen bg-gray-900 flex flex-col">
        {/* All-aboard modal */}
        {allAboardModal && (
          <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6">
            <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center">
              <div className="text-5xl mb-3">🚌</div>
              <h2 className="text-2xl font-black text-gray-900 mb-2">
                ALL ABOARD!
              </h2>
              <p className="text-gray-600 mb-6 text-sm">
                Coordinator confirmed all passengers are on board.
              </p>
              <button
                type="button"
                className="bg-green-500 hover:bg-green-400 text-white font-bold py-3 px-8 rounded-xl w-full"
                onClick={() => setAllAboardModal(false)}
              >
                ACKNOWLEDGED
              </button>
            </div>
          </div>
        )}

        {/* Status banner */}
        {statusBanner && (
          <div className="bg-blue-600 px-4 py-3 flex items-center justify-between">
            <p className="text-white text-sm flex-1">{statusBanner}</p>
            <button
              type="button"
              aria-label="Dismiss"
              className="text-white/70 hover:text-white ml-3 text-lg leading-none"
              onClick={() => setStatusBanner(null)}
            >
              ×
            </button>
          </div>
        )}

        {/* Warnings */}
        {tokenError && (
          <div className="bg-red-700 px-4 py-3">
            <p className="text-white text-sm">
              Invalid driver token — contact ops.
            </p>
          </div>
        )}
        {wakeLockWarn && (
          <div className="bg-yellow-600/80 px-4 py-3">
            <p className="text-yellow-100 text-sm">
              ⚠ Screen may turn off. Keep your phone awake manually.
            </p>
          </div>
        )}

        {/* Main tracking content */}
        <div
          role="status"
          aria-live="polite"
          aria-label="Tracking active"
          className="flex-1 flex flex-col items-center justify-center p-6 gap-6"
        >
          {gpsError ? (
            <div className="bg-red-900/50 border border-red-500/30 rounded-xl p-6 max-w-sm w-full text-center">
              <div className="text-4xl mb-3">📍</div>
              <h2 className="text-white font-bold mb-2">Location Required</h2>
              <p className="text-gray-300 text-sm">
                Location access was denied. Please enable it in your browser or
                device settings, then reload the page.
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <span
                  aria-hidden="true"
                  className="w-4 h-4 rounded-full bg-green-500 animate-pulse"
                />
                <span className="text-green-400 text-2xl font-bold tracking-wide">
                  TRACKING ACTIVE
                </span>
              </div>
              <p className="text-gray-400 text-sm text-center">
                Your location is being shared. Keep your phone plugged in.
              </p>
            </>
          )}

          {coordDistance && (
            <div className="bg-gray-800 rounded-xl px-5 py-3 text-center">
              <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">
                Coordinator
              </p>
              <p className="text-white font-semibold">
                {coordDistance} away
              </p>
              {coordInfo?.label && (
                <p className="text-gray-400 text-xs mt-0.5 truncate max-w-[200px]">
                  📍 {coordInfo.label}
                </p>
              )}
            </div>
          )}

          {session && (
            <p className="text-gray-600 text-xs">
              Bus {session.busNumber} · {session.eventName}
            </p>
          )}
        </div>
      </div>
    );
  }

  // state === "ready"
  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-center p-6 gap-6">
        <div className="text-center">
          <p className="text-yellow-400 text-sm font-semibold uppercase tracking-widest mb-1">
            Bus Driver
          </p>
          <h1 className="text-4xl font-black text-white">
            Bus {session!.busNumber}
          </h1>
          <p className="text-gray-400 mt-2 text-lg">{session!.eventName}</p>
        </div>

        <div className="bg-yellow-400/10 border border-yellow-400/30 rounded-xl px-5 py-4 max-w-sm w-full">
          <div className="flex items-start gap-3">
            <span aria-hidden="true" className="text-yellow-400 text-xl">
              ⚡
            </span>
            <p className="text-yellow-200 text-sm leading-relaxed">
              Keep your phone <strong>plugged in</strong> and{" "}
              <strong>screen on</strong> once tracking starts.
            </p>
          </div>
        </div>

        {/* Primary: jump into the native Driver app via the custom scheme. */}
        <a
          href={appDeepLink}
          className="bg-yellow-400 hover:bg-yellow-300 active:scale-95 text-gray-900 font-black text-xl py-5 px-12 rounded-2xl shadow-lg shadow-yellow-400/20 transition-all w-full max-w-sm text-center"
        >
          Open in Driver App
        </a>

        {/* Fallback: track in this browser (no app installed, or the scheme
            didn't resolve). Same GPS sharing, foreground only. */}
        <button
          type="button"
          className="text-gray-400 hover:text-gray-200 text-sm underline underline-offset-4"
          onClick={startTracking}
        >
          Don't have the app? Track in this browser
        </button>
      </div>

      <p className="text-center text-gray-600 text-xs pb-6">
        RutaLink Fleet Tracker
      </p>
    </div>
  );
}
